import { Component, OnDestroy, OnInit } from "@angular/core";
import { Subscription } from "rxjs";
import {
  faUpload,
  faCancel,
  faTrash,
  faXmark,
  faCheck,
} from "@fortawesome/free-solid-svg-icons";
import { SpecialPriceListMasterService } from "src/app/services/sales/special-price-list-master.service";
import { NotifierService } from "angular-notifier";
import { environment } from "src/environments/environment";

@Component({
  selector: "app-manage-files",
  templateUrl: "./manage-files.component.html",
  styleUrls: ["./manage-files.component.css"],
})
export class ManageFilesComponent implements OnInit, OnDestroy {
  constructor(
    private specialPriceListMasterService: SpecialPriceListMasterService,
    private notifier: NotifierService,
  ) {}

  faUpload = faUpload;
  faCancel = faCancel;
  faTrash = faTrash;
  faXmark = faXmark;
  faCheck = faCheck;
  private uploadSubscriptions = new Map<string, Subscription>();
  private readonly maxFileSize = 10 * 1024 * 1024;
  private readonly maxFileCount = 10;

  MainEntries: any[] = [];

  MainHeaders = [
    { title: "FILE NAME", type: "data", id: "Name" },
    {
      title: "DOWNLOAD",
      type: "save",
      id: "Download",
      Hclasses: "text-center",
    },
  ];

  SecondaryHeaders = [
    "NAME",
    "SIZE",
    "PROGRESS",
    "STATUS",
    "ACTIONS",
  ];

  SecondaryEntries: any[] = [];

  files: File[] = [];

  totalProgress: number = 0;

  ngOnInit() {
    this.getAllFiles();
  }

  ngOnDestroy(): void {
    this.uploadSubscriptions.forEach((subscription) =>
      subscription.unsubscribe(),
    );
    this.uploadSubscriptions.clear();
  }

  private getAllFiles(): void {
    this.specialPriceListMasterService.GetAllFiles().subscribe(
      (response) => {
        this.MainEntries = response.body.map((obj: any) => {
          const newObj: any = {
            Name: obj.Name,
            Download: "",
          };
          return newObj;
        });
      },
      (error) => {
        console.error("Error fetching data:", error);
        this.notifier.notify(
          "error",
          "Unable to load Special Price List files",
        );
      },
    );
  }

  openFile(event: any): void {
    const file = encodeURIComponent(event.Name);
    window.open(
      `${environment.KAPS_API_BACKEND_URL}SpecialPriceListMasterService.asmx/DownloadFile?fileName=${file}`,
      "_blank",
    );
  }

  uploadEntries(entry?: string): void {
    const uploadedFiles = this.files.filter(
      (file) =>
        (!entry || file.name === entry) &&
        this.getEntry(file.name)?.Status !== "uploading" &&
        this.getEntry(file.name)?.Status !== "success",
    );

    uploadedFiles.forEach((file) => {
      const activeEntry = this.getEntry(file.name);
      if (!activeEntry) {
        return;
      }
      activeEntry.Status = "uploading";

      const subscription = this.specialPriceListMasterService
        .UploadFiles(file)
        .subscribe({
          next: (response) => {
            if (response.Code === undefined && response.Name) {
              activeEntry.Status = "success";
              activeEntry.Progress = 1;
              this.changeProgress();
              this.getAllFiles();
              this.notifier.notify(
                "success",
                "Document has been uploaded successfully",
              );
            } else {
              activeEntry.Status = "error";
              this.notifier.notify(
                "error",
                response.Data || "Unable to upload document",
              );
            }
          },
          error: (error) => {
            console.error(error);
            activeEntry.Status = "error";
            this.uploadSubscriptions.delete(file.name);
            this.notifier.notify(
              "error",
              `Unable to upload ${file.name}`,
            );
          },
          complete: () => {
            this.uploadSubscriptions.delete(file.name);
          },
        });
      this.uploadSubscriptions.set(file.name, subscription);
    });
  }

  cancelUpload(entry?: string): void {
    const names = entry
      ? [entry]
      : Array.from(this.uploadSubscriptions.keys());

    names.forEach((name) => {
      this.uploadSubscriptions.get(name)?.unsubscribe();
      this.uploadSubscriptions.delete(name);
      const activeEntry = this.getEntry(name);
      if (activeEntry?.Status === "uploading") {
        activeEntry.Status = "cancelled";
        activeEntry.Progress = 0;
      }
    });
    this.changeProgress();
  }

  private changeProgress(): void {
    const totalProgress = this.SecondaryEntries.reduce(
      (total, item) => total + (item.Progress || 0),
      0,
    );

    const entriesLength = this.SecondaryEntries.length;
    this.totalProgress =
      entriesLength > 0 ? totalProgress / entriesLength : 0;
  }

  isCancelDisabled(): boolean {
    return !this.SecondaryEntries.some(
      (item) => item.Status === "uploading",
    );
  }

  isUploadDisabled(): boolean {
    return !this.SecondaryEntries.some(
      (item) =>
        item.Status !== "uploading" && item.Status !== "success",
    );
  }

  onFileChange(event: any): void {
    this.files = [];
    this.SecondaryEntries = [];

    const target = event.target as HTMLInputElement;

    if (!target.files || target.files.length === 0) {
      console.log("No file selected");
      return;
    }

    const selectedFiles = Array.from(target.files).slice(
      0,
      this.maxFileCount,
    );
    const validFiles = selectedFiles.filter((file) => {
      if (file.size > this.maxFileSize) {
        this.notifier.notify(
          "error",
          `${file.name} exceeds the 10 MB limit`,
        );
        return false;
      }
      return true;
    });

    if (target.files.length > this.maxFileCount) {
      this.notifier.notify(
        "error",
        `A maximum of ${this.maxFileCount} files can be queued`,
      );
    }
    this.files = validFiles;

    validFiles.forEach((file) => {
      const newEntry = {
        Name: file.name,
        Size: `${(file.size * Math.pow(10, -6)).toFixed(2)} MB`,
        Progress: 0,
        Status: "pending",
      };

      this.SecondaryEntries = [...this.SecondaryEntries, newEntry];
    });
  }

  removeEntry(entry?: any): void {
    if (entry) {
      this.cancelUpload(entry);
      this.SecondaryEntries = this.SecondaryEntries.filter(
        (item) => item.Name !== entry,
      );
      this.files = this.files.filter((item) => item.name !== entry);
    } else {
      this.cancelUpload();
      this.SecondaryEntries = [];
      this.files = [];
    }
    this.changeProgress();
  }

  private getEntry(name: string): any {
    return this.SecondaryEntries.find((item) => item.Name === name);
  }
}
